import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Input,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Card,
  Alert
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { BRAND } from '../brand';
import MaterialPopup from '../components/MaterialPopup';
import AddMaterialModal from '../components/AddMaterialModal';
import StockListCard from '../components/StockListCard';
import StockMobileList from '../components/StockMobileList';
import StockPageLayout from '../components/StockPageLayout';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import StockAddHeaderButton from '../components/StockAddHeaderButton';
import useIsMobile from '../hooks/useIsMobile';
import { encodeRouteParam } from '../util/routeParams';

const SORT_OPTIONS = {
  material_name: { label: 'Material Name', param: 'material_name' },
  brand_name: { label: 'Brand', param: 'brand_name' },
  created_by: { label: 'Created By', param: 'created_by' },
  total_quantity: { label: 'Total Quantity', param: 'total_quantity' },
  earliest_expiry: { label: 'Days to Expire', param: 'earliest_expiry' }
};

const StockList = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('material_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        sortBy,
        sortOrder
      };
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/materials', { params });
      setMaterials(response.data.materials || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch materials');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const SortableHeader = ({ column, children, style = {} }) => (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => handleSort(column)}
      title={`Sort by ${SORT_OPTIONS[column]?.label || column}`}
    >
      {children}
      {sortBy === column && (
        <span style={{ marginLeft: 4, opacity: 0.9 }}>
          {sortOrder === 'asc' ? ' ▲' : ' ▼'}
        </span>
      )}
    </th>
  );

  const handleMaterialClick = (materialName) => {
    if (isMobile) {
      navigate(`/stock/${encodeRouteParam(materialName)}`);
      return;
    }
    setSelectedMaterial(materialName);
  };

  const handleClosePopup = () => {
    setSelectedMaterial(null);
    fetchMaterials();
  };

  const headerAction = useMemo(
    () => (
      <StockAddHeaderButton
        label="Add material"
        onClick={() => setAddModalOpen(true)}
      />
    ),
    []
  );

  useSetPageHeaderAction(headerAction);

  const totalPages = Math.ceil(materials.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMaterials = materials.slice(startIndex, startIndex + itemsPerPage);

  const getDaysToExpire = (expiryDate, asOfDate) => {
    if (!expiryDate) return 'N/A';
    const expiry = new Date(expiryDate);
    if (Number.isNaN(expiry.getTime())) return 'N/A';
    const ref = asOfDate ? new Date(asOfDate) : new Date();
    const ms = expiry.getTime() - ref.getTime();
    const days = Math.round(ms / (24 * 60 * 60 * 1000));
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`;
    return 'Expires today';
  };

  const paginationBar = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography level="body-md">
        Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, materials.length)} of {materials.length} materials
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage(currentPage + 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
    <StockPageLayout
      error={error && <Alert color="danger">{error}</Alert>}
      loading={loading}
      search={(
        <Card sx={{ mb: { xs: 0, md: 3 }, p: 2 }}>
          <FormControl>
            <FormLabel>Search by Material Name</FormLabel>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search materials..."
              sx={{
                '&:hover': { borderColor: 'primary.500' },
                '&:focus-within': { borderColor: 'primary.500' }
              }}
            />
          </FormControl>
        </Card>
      )}
      pagination={paginationBar}
    >
          <StockMobileList empty={paginatedMaterials.length === 0} emptyMessage="No materials found">
            {paginatedMaterials.map((material, index) => (
              <StockListCard
                key={`${material.material_name}-${index}`}
                title={material.material_name}
                onClick={() => handleMaterialClick(material.material_name)}
                fields={[
                  { label: 'Brands', value: material.brand_name || '—' },
                  {
                    label: 'Total Quantity',
                    value: (
                      <>
                        <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                          {material.total_quantity}
                        </Typography>
                        {Number(material.total_quantity) === 0 && (
                          <Typography level="body-xs" sx={{ color: 'text.tertiary', display: 'block' }}>
                            (Out of stock)
                          </Typography>
                        )}
                      </>
                    )
                  },
                  { label: 'Days to Expire', value: getDaysToExpire(material.earliest_expiry) },
                  { label: 'Vendor', value: material.vendor_name || '—' },
                  { label: 'Created By', value: material.created_by || '—' }
                ]}
              />
            ))}
          </StockMobileList>

          <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: 'primary.500',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: 'primary.50',
                  cursor: 'pointer'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <SortableHeader column="material_name" style={{ width: '30%' }}>
                    Material Name
                  </SortableHeader>
                  <SortableHeader column="brand_name" style={{ width: '20%' }}>
                    Brands
                  </SortableHeader>
                  <SortableHeader column="total_quantity" style={{ width: '25%' }}>
                    Total Quantity
                  </SortableHeader>
                  <SortableHeader column="earliest_expiry" style={{ width: '25%' }}>
                    Days to Expire
                  </SortableHeader>
                  <th style={{ width: '20%' }}>Vendor Name</th>
                  <SortableHeader column="created_by" style={{ width: '20%' }}>
                    Created By
                  </SortableHeader>
                </tr>
              </thead>
              <tbody>
                {paginatedMaterials.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                      No materials found
                    </td>
                  </tr>
                ) : (
                  paginatedMaterials.map((material, index) => (
                    <tr
                      key={index}
                      onClick={() => handleMaterialClick(material.material_name)}
                    >
                      <td style={{ color: BRAND.colors.navy, fontWeight: 'bold' }}>
                        {material.material_name}
                      </td>
                      <td>{material.brand_name || '—'}</td>
                      <td>
                        {material.total_quantity}
                        {Number(material.total_quantity) === 0 && (
                          <Typography component="span" level="body-xs" sx={{ color: 'text.tertiary', ml: 0.5 }}>
                            (Out of stock)
                          </Typography>
                        )}
                      </td>
                      <td>
                        {getDaysToExpire(material.earliest_expiry)}
                      </td>
                      <td>{material.vendor_name || '—'}</td>
                      <td>{material.created_by || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
    </StockPageLayout>

      {selectedMaterial && (
        <MaterialPopup
          materialName={selectedMaterial}
          onClose={handleClosePopup}
        />
      )}

      <AddMaterialModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={fetchMaterials}
      />
    </>
  );
};

export default StockList;

